/**
 * The picker's auto-distribute maths, kept out of the take table so it can be reasoned about and
 * tested on its own (plan §1.1 "Auto-distribute in the picker").
 *
 * It mirrors the spot sale's auto-select (`inventory_selection/autoSelectAllocation.ts`) as closely
 * as the domain allows: greedy walks the rows in a fixed order taking `min(capacity, remaining)`,
 * spread is a largest-remainder (Hamilton) split proportional to capacity, and both are scoped to
 * the rows the caller passes — the rows currently on screen. Takes on rows outside that set are
 * carried over untouched, so filtering, distributing, filtering again and distributing again
 * accumulates rather than replaces.
 *
 * The one domain difference from the spot sale: mandates have no production month to sort by, so the
 * greedy order is the cheapest volume first — lowest floor, with an unpriced mandate (no floor at all)
 * ahead of every priced one.
 */

/** What the algorithm needs of a take-table row; `PickerMandateRow` satisfies it. */
export interface DistributableRow {
  mandate_id: number;
  /** The take ceiling over the picked range — `capacity_by_period` does not sum to it. */
  capacity_total: number;
  /** Decimal string as the wire carries it; null means the mandate has no floor. */
  minimum_price: string | null;
}

/** Mandate id → MWh taken. Mandates with nothing taken are absent rather than zero. */
export type TakeMap = Record<number, number>;

/**
 * Distributes `target` MWh over `rows`, replacing whatever those rows held before.
 *
 * @param rows - the rows in scope (the filtered ones); rows with no capacity are skipped.
 * @param target - MWh wanted from these rows, floored to whole MWh; below one clears their takes.
 * @param spread - proportional over every row instead of filling the cheapest first.
 * @param currentTakes - the takes to start from; entries for rows outside `rows` survive.
 * @returns a new take map — the argument is never mutated.
 */
export function distributeTakes(
  rows: readonly DistributableRow[],
  target: number,
  spread: boolean,
  currentTakes: Readonly<TakeMap>
): TakeMap {
  const result: TakeMap = { ...currentTakes };
  for (const row of rows) delete result[row.mandate_id];

  // A take is whole MWh (`takes: dict[int, int]` on the backend), so a fractional target is floored
  // once here rather than left to leak into every branch below (RV-picker-03).
  const wanted = Math.floor(target);
  if (wanted <= 0) return result;

  const eligible = orderForGreedy(rows.filter((row) => row.capacity_total > 0));
  const takes = spread
    ? spreadOverMandates(eligible, wanted)
    : fillCheapestFirst(eligible, wanted);

  for (const [mandateId, volume] of takes) {
    if (volume > 0) result[mandateId] = volume;
  }
  return result;
}

/**
 * Splits one mandate's take over the picked periods, proportionally to each period's capacity.
 *
 * An estimate, not the split the deal will be created with: the backend divides top-down over the
 * mandate's listing tree (`availability_service.split_take`), which the take table's per-period caps
 * do not describe. It agrees with the backend whenever the picked periods are the mandate's own
 * listing periods, which is the ordinary case, and is close otherwise — enough for a summary that
 * says so.
 *
 * @returns period label → MWh, periods with nothing left out; empty when the take is not positive.
 */
export function splitTakeOverPeriods(
  take: number,
  capacityByPeriod: Readonly<Record<string, number>>
): Record<string, number> {
  if (take <= 0) return {};

  const periods = Object.entries(capacityByPeriod)
    .filter(([, capacity]) => capacity > 0)
    .sort(([left], [right]) => left.localeCompare(right));
  if (periods.length === 0) return {};

  const shares = largestRemainder(
    take,
    periods.map(([, capacity]) => capacity)
  );

  const split: Record<string, number> = {};
  periods.forEach(([label], index) => {
    if (shares[index] > 0) split[label] = shares[index];
  });
  return split;
}

/** Cheapest volume first: no floor, then the lowest floor, then the biggest mandate, then the oldest. */
function orderForGreedy(rows: readonly DistributableRow[]): DistributableRow[] {
  return [...rows].sort((left, right) => {
    const leftFloor = floorOf(left);
    const rightFloor = floorOf(right);
    if (leftFloor !== rightFloor) return leftFloor - rightFloor;
    if (left.capacity_total !== right.capacity_total) {
      return right.capacity_total - left.capacity_total;
    }
    return left.mandate_id - right.mandate_id;
  });
}

/** A mandate without a floor sorts ahead of every priced one, so `-Infinity` rather than 0. */
function floorOf(row: DistributableRow): number {
  if (row.minimum_price === null) return Number.NEGATIVE_INFINITY;
  const parsed = Number(row.minimum_price);
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
}

function fillCheapestFirst(
  ordered: readonly DistributableRow[],
  target: number
): Map<number, number> {
  const takes = new Map<number, number>();
  let remaining = target;
  for (const row of ordered) {
    if (remaining <= 0) break;
    const take = Math.min(row.capacity_total, remaining);
    takes.set(row.mandate_id, take);
    remaining -= take;
  }
  return takes;
}

function spreadOverMandates(
  ordered: readonly DistributableRow[],
  target: number
): Map<number, number> {
  const totalCapacity = ordered.reduce(
    (sum, row) => sum + row.capacity_total,
    0
  );
  if (target >= totalCapacity) {
    return new Map(ordered.map((row) => [row.mandate_id, row.capacity_total]));
  }

  const shares = largestRemainder(
    target,
    ordered.map((row) => row.capacity_total)
  );
  return new Map(ordered.map((row, index) => [row.mandate_id, shares[index]]));
}

/**
 * Hamilton's method: floor every quota, then hand the leftover units out one at a time to the
 * largest fractional remainders, breaking ties by weight descending and then by position. The spot
 * sale's spread over companies (`inventory_selection/autoSelectAllocation.ts`) shares out with it
 * too, so the two behave alike on the same numbers.
 *
 * @returns one share per weight, in the weights' own order, each capped at its weight.
 */
export function largestRemainder(
  total: number,
  weights: readonly number[]
): number[] {
  const weightSum = weights.reduce((sum, weight) => sum + weight, 0);
  if (weightSum <= 0) return weights.map(() => 0);
  if (total >= weightSum) return [...weights];

  const shares: number[] = [];
  const remainders: { index: number; remainder: number; weight: number }[] = [];
  let assigned = 0;

  weights.forEach((weight, index) => {
    const quota = (total * weight) / weightSum;
    const share = Math.floor(quota);
    shares.push(share);
    assigned += share;
    remainders.push({ index, remainder: quota - share, weight });
  });

  remainders.sort((left, right) => {
    if (left.remainder !== right.remainder) {
      return right.remainder - left.remainder;
    }
    if (left.weight !== right.weight) return right.weight - left.weight;
    return left.index - right.index;
  });

  // `> 0` rather than `=== 0`: a fractional total would never hit zero and would hand out an extra
  // unit to every row (RV-picker-03).
  let deficit = total - assigned;
  for (const { index, weight } of remainders) {
    if (deficit <= 0) break;
    if (shares[index] < weight) {
      shares[index] += 1;
      deficit -= 1;
    }
  }
  return shares;
}
